import { useState, useEffect } from "react";
import { Box, Heading, Text, Tag, Spinner } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { gql } from "__generated__/gql";
import { SubscriptionStatus } from "__generated__/serverGql/graphql";
import { ColorfulBooleanTag } from "components/Shared/BooleanTag";
import BrandName from "components/Shared/BrandName";
import ContactEmailLink from "components/Shared/ContactEmailLink";
import EmailLink from "components/Shared/EmailLink";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { asyncVoid } from "utils/asyncVoidHandler";
import { APP_DOMAIN, APP_NAME } from "utils/config";
import { useCurrentBrandId } from "utils/currentUserHooks";
import {
  useClearRouteParamsOnBrandChange,
  useRouteParam,
} from "utils/routeParamHooks";
import CheckoutSessionCompletionAlert from "./CheckoutSessionCompletionAlert";
import StartSubscriptionButton from "./StartSubscriptionButton";
import StripePortalButton from "./StripePortalButton";
import SubscriptionStatusAlert from "./SubscriptionStatusAlert";
import useRefreshSubscriptionStatus from "./useRefreshSubscriptionStatus";

const query = gql(/* GraphQL */ `
  query BrandBillingPage($brandId: UUID!) {
    brand(id: $brandId) {
      id
      isSubscriptionValid
      stripeCustomerId
      stripePriceId
      stripeSubscriptionId
      ...StripePortalButton
      ...StartSubscriptionButton
    }
  }
`);

export default function Billing() {
  const brandId = useCurrentBrandId();
  const { isReady } = useRouter();
  const [sessionIdFromUrl, setSessionId] = useRouteParam("session_id");
  const [localSessionId, setLocalSessionId] = useState(sessionIdFromUrl);
  useClearRouteParamsOnBrandChange(["session_id"]);

  useEffect(() => {
    if (sessionIdFromUrl && !localSessionId) {
      setLocalSessionId(sessionIdFromUrl);
    }
  }, [sessionIdFromUrl, localSessionId]);

  const { data, loading: brandLoading } = useBrandQuery(query, {
    skip: !brandId,
    variables: brandId ? { brandId } : undefined,
    returnPartialData: true,
  });

  const brand = data?.brand;

  const { subscriptionStatus, mutationResult } = useRefreshSubscriptionStatus({
    brandId,
    stripeSubscriptionId: brand?.stripeSubscriptionId,
    skip: !isReady || !!sessionIdFromUrl || !!localSessionId,
  });

  const hasStripe = !!brand?.stripeCustomerId;
  const hasSubscription = !!brand?.stripeSubscriptionId;
  const canSubscribe = hasStripe && !!brand?.stripePriceId;

  // No subscription yet but can subscribe - needs initial setup
  const shouldShowInitialSetup = !hasSubscription && canSubscribe;

  // Has invalid subscription - show status and maybe allow resubscribe
  const hasInvalidSubscription = hasSubscription && !brand?.isSubscriptionValid;

  // Terminal states allow new subscription
  const canResubscribe =
    hasInvalidSubscription &&
    canSubscribe &&
    (subscriptionStatus === SubscriptionStatus.CANCELED ||
      subscriptionStatus === SubscriptionStatus.INCOMPLETE_EXPIRED);

  return (
    <>
      {/* Process checkout session if present */}
      {localSessionId && (
        <CheckoutSessionCompletionAlert
          sessionId={localSessionId}
          onProcessed={() => {
            asyncVoid(setSessionId(null));
          }}
        />
      )}

      {/* Show subscription status alert for subscriptions (but not during checkout) */}
      {!localSessionId && (
        <SubscriptionStatusAlert status={subscriptionStatus || null} />
      )}

      {/* Status Section */}
      <Box mt={6} fontSize="lg">
        The subscription for <BrandName fontWeight="semibold" /> is:{" "}
        {shouldShowInitialSetup ? (
          <Tag
            colorScheme="blue"
            fontSize="1em"
            py=".25em"
            verticalAlign="baseline"
          >
            Pending…
          </Tag>
        ) : (
          <ColorfulBooleanTag
            value={brand?.isSubscriptionValid}
            trueLabel="Valid"
            falseLabel="Invalid"
            fontWeight="semibold"
            fontSize="1em"
            py=".25em"
            verticalAlign="baseline"
          />
        )}
        {(mutationResult.loading ||
          brandLoading ||
          typeof window === "undefined") && <Spinner ml={3} color="gray" />}
      </Box>

      <Text color="gray.500" fontSize="sm" mt={2} mb={7}>
        {!shouldShowInitialSetup &&
          brand?.isSubscriptionValid &&
          `Thank you for using ${APP_NAME}!`}
        {!shouldShowInitialSetup && !brand?.isSubscriptionValid && (
          <>
            Please contact <ContactEmailLink fontWeight="semibold" /> to
            reactivate your subscription to {APP_NAME}.
          </>
        )}
      </Text>

      {/* Action Section - Show appropriate button or message */}
      {!hasStripe && (
        // External billing
        <Text>
          For questions about {brand?.isSubscriptionValid ? "" : "past "}billing
          {brand?.isSubscriptionValid
            ? " or to update billing details"
            : ""}{" "}
          please contact{" "}
          <EmailLink email={`accounting@${APP_DOMAIN}`} fontWeight="semibold" />
          .
        </Text>
      )}

      {hasStripe && brand && (
        <>
          {(shouldShowInitialSetup || canResubscribe) && (
            <>
              <Text color="gray.500" fontSize="sm" mb={1}>
                {shouldShowInitialSetup
                  ? "To activate your subscription, please start your subscription by completing the checkout process below."
                  : "Start a new subscription:"}
              </Text>
              <StartSubscriptionButton brand={brand} />
            </>
          )}

          {!shouldShowInitialSetup && (
            <>
              <Text color="gray.500" fontSize="sm" mb={1}>
                {canResubscribe ? "Or update" : "To update"} your billing
                details, please use the {APP_NAME} billing portal.
              </Text>
              <StripePortalButton brand={brand} />
            </>
          )}
        </>
      )}

      {brand?.isSubscriptionValid ? (
        <>
          <Heading as="h2" size="sm" mt={12} mb={2} color="magenta.veryDark">
            Subscription Changes & Cancellation
          </Heading>
          <Text>
            To make changes to your subscription or cancel it, please contact{" "}
            <ContactEmailLink fontWeight="semibold" />.
          </Text>
        </>
      ) : (
        <>
          <Heading as="h2" size="sm" mt={12} mb={2} color="magenta.veryDark">
            Need Help?
          </Heading>
          <Text>
            For questions about your subscription, please contact{" "}
            <ContactEmailLink fontWeight="semibold" />.
          </Text>
        </>
      )}
    </>
  );
}
